/* eslint-disable max-lines-per-function */
import {
  Component,
  computed,
  DestroyRef,
  inject,
  output,
  type Signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';

import { CompanySwitchStrategy } from '@core/http-legacy';
import { I18nService } from '@core/i18n-legacy';
import { CompanyVerificationDataService } from '@core/tellent-company-verification-logic';
import { TellentCurrentUserSelectors } from '@core/tellent-data';
import {
  CurrentUserProfileDialogComponent,
  provideTellentProfileDropdownConfig,
  TellentAdoptedStylesheetsDirective,
  TellentThemeDirective,
  TellentProfileDropdownComponent,
} from '@core/tellent-smart-ui';
import type {
  EventEmitterLike,
  TellentCurrentUser,
  TellentWebComponent,
} from '@core/tellent-types';
import { APP_GUID, UserProfileTab } from '@core/tellent-types';
import { DialogService, DialogWidth, RtPlatform } from '@core/user-interface-legacy';

class ProfileAfterCloseNotifier extends Subject<any> {}

@Component({
  selector: 'x-profile-dropdown',
  template: `<tla-profile-dropdown /> `,
  styleUrls: ['../../../styles.less', './profile-dropdown-wrap.component.less'],
  imports: [TellentProfileDropdownComponent],
  encapsulation: ViewEncapsulation.ShadowDom,
  hostDirectives: [TellentThemeDirective, TellentAdoptedStylesheetsDirective],
  viewProviders: [
    ProfileAfterCloseNotifier,

    provideTellentProfileDropdownConfig(() => {
      const platform = inject(RtPlatform);
      const profileCloseNotifier = inject(ProfileAfterCloseNotifier);
      const appGuid = inject(APP_GUID);
      const i18n = inject(I18nService);
      const dialogSvc = inject(DialogService);
      const store = inject(Store);
      const companyVerificationDataService = inject(CompanyVerificationDataService);
      const currentUser = <Signal<TellentCurrentUser>>(
        store.selectSignal(TellentCurrentUserSelectors.data)
      );

      const switchStrategy = inject(CompanySwitchStrategy);

      const currentMember = computed(() => {
        const user = currentUser();
        return user?.members.find(m => m.seats.some(s => s.app.guid === appGuid));
      });

      const currentOrg = computed(() => {
        const user = currentUser();
        return user.members.find(m => m.org.guid === user.currentOrgGuid)?.org;
      });

      const currentSeat = computed(() => currentMember()?.seats.find(s => s.app.guid === appGuid)!);
      const companies = computed(() => currentUser()?.members);

      const companyVerificationStatus = toSignal(companyVerificationDataService.status$);

      return {
        companies,
        currentUser,
        currentMember,
        currentSeat,
        currentOrg,
        companyVerificationStatus,
        handlers: {
          switchCompany: orgGuid => switchStrategy.switchCompany(orgGuid),
          editProfile: (analyticsContext?: string) =>
            dialogSvc
              .openCustom(
                CurrentUserProfileDialogComponent,
                {
                  width: DialogWidth.Small,
                  mobileOptions: platform.IS_MOBILE
                    ? {
                        mobileDialog: true,
                        mobileViewTitle: i18n.translate('tlc.current_user.mobile_heading'),
                      }
                    : undefined,
                },
                {
                  analyticsContext,
                },
              )
              .afterClosed.subscribe(event => {
                profileCloseNotifier.next(event);
              }),
          manageCompanies: (analyticsContext?: string) => {
            const dialog = dialogSvc.openCustom(
              CurrentUserProfileDialogComponent,
              {
                width: DialogWidth.Small,
                mobileOptions: platform.IS_MOBILE
                  ? {
                      mobileDialog: true,
                      mobileViewTitle: i18n.translate('tlc.current_user.mobile_heading'),
                    }
                  : undefined,
              },
              {
                analyticsContext,
              },
            );
            dialog.componentInstance.setActiveTab(UserProfileTab.Companies);
          },
        },
      };
    }),
  ],
})
export class TellentProfileDropdownWrapComponent {
  #profileCloseNotifier = inject(ProfileAfterCloseNotifier);
  #destroyRef = inject(DestroyRef);

  public afterProfileDialogClosed: EventEmitterLike<any> = output<any>();

  public ngOnInit() {
    this.#profileCloseNotifier
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(e => this.afterProfileDialogClosed.emit(e));
  }
}

export type TellentProfileDropdownWebComponent = TellentWebComponent<
  TellentProfileDropdownWrapComponent,
  'afterProfileDialogClosed'
>;
